import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Edge,
  Node,
  Handle,
  Position,
  NodeProps,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { 
  Play, 
  Settings, 
  Plus, 
  Save, 
  Trash2, 
  X, 
  FolderKanban, 
  ChevronRight, 
  AlertCircle, 
  Image as ImageIcon,
  CheckCircle,
  Eye
} from 'lucide-react';
import axios from 'axios';
import appConfig from '../config';

// Base URL configuration for APIs
const API_BASE = appConfig.baseURL;

// ============================================================================
// Custom React Flow Nodes Styled with Glassmorphism & High-Quality Aesthetics
// ============================================================================

// Trigger Node
const TriggerNode = ({ data }: NodeProps) => {
  return (
    <div className="relative px-4 py-3 bg-[#1e152a]/95 border-2 border-purple-500 rounded-xl shadow-[0_0_15px_rgba(168,85,247,0.2)] text-white w-64 backdrop-blur-md">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
        <span className="text-xs uppercase font-bold tracking-wider text-purple-400">Trigger Node</span>
      </div>
      <div className="text-sm font-semibold text-gray-100 truncate">
        {data?.event || 'Select Event'}
      </div>
      <div className="text-[10px] text-gray-400 mt-1">Starts the campaign automation flow</div>
      <Handle type="source" position={Position.Right} className="w-3 h-3 bg-purple-500 border-2 border-[#1e152a]" />
    </div>
  );
};

// Condition Node
const ConditionNode = ({ data }: NodeProps) => {
  return (
    <div className="relative px-4 py-3 bg-[#0f1d3a]/95 border-2 border-blue-500 rounded-xl shadow-[0_0_15px_rgba(59,130,246,0.2)] text-white w-64 backdrop-blur-md">
      <Handle type="target" position={Position.Left} className="w-3 h-3 bg-blue-500 border-2 border-[#0f1d3a]" />
      <div className="flex items-center gap-2 mb-1">
        <div className="w-2 h-2 rounded-full bg-blue-500" />
        <span className="text-xs uppercase font-bold tracking-wider text-blue-400">Condition Node</span>
      </div>
      <div className="text-sm font-semibold text-gray-100">
        {data?.field ? `${data?.field} ${data?.operator || ''} ${data?.value ?? ''}` : 'Select Condition'}
      </div>
      <div className="text-[10px] text-gray-400 mt-1">Evaluates user criteria before actions run</div>
      <Handle type="source" position={Position.Right} className="w-3 h-3 bg-blue-500 border-2 border-[#0f1d3a]" />
    </div>
  );
};

// Action Node
const ActionNode = ({ data }: NodeProps) => {
  return (
    <div className="relative px-4 py-3 bg-[#0d2218]/95 border-2 border-emerald-500 rounded-xl shadow-[0_0_15px_rgba(16,185,129,0.2)] text-white w-64 backdrop-blur-md">
      <Handle type="target" position={Position.Left} className="w-3 h-3 bg-emerald-500 border-2 border-[#0d2218]" />
      <div className="flex items-center gap-2 mb-1">
        <div className="w-2 h-2 rounded-full bg-emerald-500" />
        <span className="text-xs uppercase font-bold tracking-wider text-emerald-400">Action Node</span>
      </div>
      <div className="text-sm font-semibold text-gray-100 truncate">
        {data?.type ? `${data?.type} (${data?.amount || data?.level || data?.tag || 'Active'})` : 'Select Action'}
      </div>
      <div className="text-[10px] text-gray-400 mt-1">Executes reward, upgrade or notification</div>
      <Handle type="source" position={Position.Right} className="w-3 h-3 bg-emerald-500 border-2 border-[#0d2218]" />
    </div>
  );
};

// Node Registration
const nodeTypes = {
  triggerNode: TriggerNode,
  conditionNode: ConditionNode,
  actionNode: ActionNode,
};

// Types & Schemas
interface Campaign {
  id?: string;
  name: string;
  category: string;
  status: string;
  priority: number;
  flow_json: {
    trigger: { event: string };
    conditions: Array<{ field: string; operator: string; value: any }>;
    actions: Array<{ type: string; [key: string]: any }>;
    repeatable?: boolean;
    claim_type?: string;
  };
  display_json: {
    title: string;
    description: string;
    banner: string;
    icon: string;
    show_on: string[];
    visible: boolean;
    sort_order: number;
  };
  start_date: string;
  end_date: string;
}

export default function CampaignBuilder() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<'flow' | 'display'>('flow');
  const [dbCurrencies, setDbCurrencies] = useState<{code: string, name: string}[]>([]);

  // Filter & Pagination State
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // React Flow state hooks
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Sidebar detailed node selection state
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  // Upload States and Handlers
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingIcon, setUploadingIcon] = useState(false);

  const handleFileUpload = async (file: File, type: 'banner' | 'icon') => {
    const setUploading = type === 'banner' ? setUploadingBanner : setUploadingIcon;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_BASE}/admin/upload-gamecard-asset`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.data?.url) {
        handleDisplayFieldChange(type, res.data.url);
      }
    } catch (err) {
      console.error(`Failed to upload ${type}:`, err);
      alert(`Failed to upload ${type}`);
    } finally {
      setUploading(false);
    }
  };

  // Fetch campaigns
  const loadCampaigns = async () => {
    try {
      const res = await axios.get(`${API_BASE}/admin/campaigns`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.data && res.data.campaigns) {
        setCampaigns(res.data.campaigns);
        return res.data.campaigns;
      }
    } catch (err) {
      console.error('Failed to load campaigns:', err);
    }
    return [];
  };

  useEffect(() => {
    const setup = async () => {
      const camps = await loadCampaigns();
      
      try {
        const cRes = await axios.get(`${API_BASE}/api/currencies`);
        if (cRes.data?.success) setDbCurrencies(cRes.data.currencies || []);
      } catch (e) {
        console.error('Failed to load currencies', e);
      }
      
      const searchParams = new URLSearchParams(window.location.search);
      const isEditorRoute = window.location.pathname.includes('/campaigns/editor');
      if (isEditorRoute) {
        const editorId = searchParams.get('id');
        const isNew = searchParams.get('mode') === 'new';
        setIsEditing(true);
        setSelectedNodeId(null);
        
        if (editorId) {
          const found = camps.find((c: any) => String(c.id) === String(editorId));
          if (found) {
            setSelectedCampaign(found);
          }
        } else if (isNew) {
          const newCamp: Campaign = {
            name: 'New Custom Promo',
            category: 'promotions',
            status: 'draft',
            priority: 0,
            flow_json: {
              trigger: { event: 'deposit_success' },
              conditions: [],
              actions: [],
              repeatable: true
            },
            display_json: {
              title: '100% Promo Reward',
              description: 'Get extra credits on successful deposit',
              banner: '/uploads/banner.jpg',
              icon: '/uploads/icon.png',
              show_on: ['promotions'],
              visible: true,
              sort_order: 1
            },
            start_date: new Date().toISOString().substring(0, 16),
            end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().substring(0, 16)
          };
          setSelectedCampaign(newCamp);
        }
      }
    };
    
    setup();

    // Reload campaigns when window gains focus (in case they edited in another tab)
    const handleFocus = () => {
      const isEditorRoute = window.location.pathname.includes('/campaigns/editor');
      if (!isEditorRoute) {
        loadCampaigns();
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Sync canvas nodes/edges when selectedCampaign changes
  useEffect(() => {
    if (!selectedCampaign) {
      setNodes([]);
      setEdges([]);
      setSelectedNodeId(null);
      return;
    }

    const { flow_json } = selectedCampaign;
    const initialNodes: Node[] = [];
    const initialEdges: Edge[] = [];

    // 1. Add Trigger Node
    initialNodes.push({
      id: 'trigger',
      type: 'triggerNode',
      position: { x: 50, y: 150 },
      data: { event: flow_json.trigger?.event || '' },
    });

    let currentX = 370;

    // 2. Add Condition Nodes
    const conditions = flow_json.conditions || [];
    conditions.forEach((cond, idx) => {
      const nodeId = `condition-${idx}`;
      initialNodes.push({
        id: nodeId,
        type: 'conditionNode',
        position: { x: currentX, y: 150 },
        data: { ...cond },
      });

      // Link previous to current
      const sourceId = idx === 0 ? 'trigger' : `condition-${idx - 1}`;
      initialEdges.push({
        id: `e-${sourceId}-${nodeId}`,
        source: sourceId,
        target: nodeId,
        animated: true,
        style: { stroke: '#3b82f6', strokeWidth: 2 },
      });

      currentX += 320;
    });

    // 3. Add Action Nodes
    const actions = flow_json.actions || [];
    actions.forEach((act, idx) => {
      const nodeId = `action-${idx}`;
      initialNodes.push({
        id: nodeId,
        type: 'actionNode',
        position: { x: currentX, y: 150 },
        data: { ...act },
      });

      // Link previous (last condition or previous action)
      const sourceId = idx === 0 
        ? (conditions.length > 0 ? `condition-${conditions.length - 1}` : 'trigger')
        : `action-${idx - 1}`;

      initialEdges.push({
        id: `e-${sourceId}-${nodeId}`,
        source: sourceId,
        target: nodeId,
        animated: true,
        style: { stroke: '#10b981', strokeWidth: 2 },
      });

      currentX += 320;
    });

    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [selectedCampaign, setNodes, setEdges]);

  // Handle Flow Connections
  const onConnect = useCallback((params: Connection) => {
    setEdges((eds) => addEdge(params, eds));
  }, [setEdges]);

  // Trigger Node select handler
  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    setSelectedNodeId(node.id);
  }, []);

  // Add Node Helper
  const addFlowElement = (type: 'condition' | 'action') => {
    if (!selectedCampaign) return;

    const currentCampaign = { ...selectedCampaign };
    if (type === 'condition') {
      const updatedConditions = [...(currentCampaign.flow_json.conditions || [])];
      updatedConditions.push({ field: 'deposit_amount', operator: '>=', value: 1000 });
      currentCampaign.flow_json.conditions = updatedConditions;
    } else {
      const updatedActions = [...(currentCampaign.flow_json.actions || [])];
      updatedActions.push({ type: 'wallet_credit', amount: 500, currency: 'inr' });
      currentCampaign.flow_json.actions = updatedActions;
    }

    setSelectedCampaign(currentCampaign);
  };

  // Remove Node Helper
  const removeFlowElement = (nodeId: string) => {
    if (!selectedCampaign) return;

    const currentCampaign = { ...selectedCampaign };
    if (nodeId.startsWith('condition-')) {
      const idx = parseInt(nodeId.split('-')[1]);
      const updatedConditions = [...(currentCampaign.flow_json.conditions || [])];
      updatedConditions.splice(idx, 1);
      currentCampaign.flow_json.conditions = updatedConditions;
    } else if (nodeId.startsWith('action-')) {
      const idx = parseInt(nodeId.split('-')[1]);
      const updatedActions = [...(currentCampaign.flow_json.actions || [])];
      updatedActions.splice(idx, 1);
      currentCampaign.flow_json.actions = updatedActions;
    }

    setSelectedCampaign(currentCampaign);
  };

  // Node parameter edit handler
  const updateNodeData = (nodeId: string, updatedFields: any) => {
    if (!selectedCampaign) return;
    const currentCampaign = { ...selectedCampaign };

    if (nodeId === 'trigger') {
      currentCampaign.flow_json.trigger = {
        ...currentCampaign.flow_json.trigger,
        ...updatedFields
      };
    } else if (nodeId.startsWith('condition-')) {
      const idx = parseInt(nodeId.split('-')[1]);
      const updatedConditions = [...(currentCampaign.flow_json.conditions || [])];
      updatedConditions[idx] = { ...updatedConditions[idx], ...updatedFields };
      currentCampaign.flow_json.conditions = updatedConditions;
    } else if (nodeId.startsWith('action-')) {
      const idx = parseInt(nodeId.split('-')[1]);
      const updatedActions = [...(currentCampaign.flow_json.actions || [])];
      updatedActions[idx] = { ...updatedActions[idx], ...updatedFields };
      currentCampaign.flow_json.actions = updatedActions;
    }

    setSelectedCampaign(currentCampaign);
  };

  // Form Field Changers
  const handleCampaignFieldChange = (field: keyof Campaign, val: any) => {
    if (!selectedCampaign) return;
    setSelectedCampaign({
      ...selectedCampaign,
      [field]: val
    });
  };

  const handleDisplayFieldChange = (field: string, val: any) => {
    if (!selectedCampaign) return;
    setSelectedCampaign({
      ...selectedCampaign,
      display_json: {
        ...selectedCampaign.display_json,
        [field]: val
      }
    });
  };

  // Create Campaign Mode
  const initNewCampaign = () => {
    const newCamp: Campaign = {
      name: 'New Custom Promo',
      category: 'promotions',
      status: 'draft',
      priority: 0,
      flow_json: {
        trigger: { event: 'deposit_success' },
        conditions: [],
        actions: [],
        repeatable: true
      },
      display_json: {
        title: '100% Promo Reward',
        description: 'Get extra credits on successful deposit',
        banner: '/uploads/banner.jpg',
        icon: '/uploads/icon.png',
        show_on: ['promotions'],
        visible: true,
        sort_order: 1
      },
      start_date: new Date().toISOString().substring(0, 16),
      end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().substring(0, 16)
    };

    setSelectedCampaign(newCamp);
    setIsEditing(true);
    setSelectedNodeId(null);
  };

  // Save Campaign to backend
  const saveCampaign = async () => {
    if (!selectedCampaign) return;

    try {
      const payload = {
        ...selectedCampaign,
        priority: Number(selectedCampaign.priority),
        flow_json: selectedCampaign.flow_json,
        display_json: selectedCampaign.display_json
      };

      let res;
      if (selectedCampaign.id) {
        // Edit Mode
        res = await axios.put(`${API_BASE}/admin/campaigns/${selectedCampaign.id}`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      } else {
        // Create Mode
        res = await axios.post(`${API_BASE}/admin/campaigns`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      }

      if (res.data.success) {
        const isEditorRoute = window.location.pathname.includes('/campaigns/editor');
        if (isEditorRoute) {
          window.close();
        } else {
          setIsEditing(false);
          setSelectedCampaign(null);
          loadCampaigns();
        }
      }
    } catch (err) {
      console.error('Error saving campaign:', err);
      alert('Failed to save campaign. Please try again.');
    }
  };

  // Delete Campaign
  const deleteCampaign = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;

    try {
      await axios.delete(`${API_BASE}/admin/campaigns/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      loadCampaigns();
      if (selectedCampaign?.id === id) {
        setSelectedCampaign(null);
        setIsEditing(false);
      }
    } catch (err) {
      console.error('Failed to delete campaign:', err);
    }
  };

  const selectedNodeData = useMemo(() => {
    if (!selectedNodeId || !selectedCampaign) return null;

    if (selectedNodeId === 'trigger') {
      return { type: 'trigger', data: selectedCampaign.flow_json.trigger as any };
    }
    if (selectedNodeId.startsWith('condition-')) {
      const idx = parseInt(selectedNodeId.split('-')[1]);
      return { type: 'condition', data: selectedCampaign.flow_json.conditions[idx] as any };
    }
    if (selectedNodeId.startsWith('action-')) {
      const idx = parseInt(selectedNodeId.split('-')[1]);
      return { type: 'action', data: selectedCampaign.flow_json.actions[idx] as any };
    }
    return null;
  }, [selectedNodeId, selectedCampaign]);


  const categories = useMemo(() => {
    const cats = new Set(campaigns.map(c => c.category));
    return Array.from(cats).filter(Boolean);
  }, [campaigns]);

  const filteredCampaigns = useMemo(() => {
    return campaigns.filter(camp => {
      const matchesSearch = camp.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                            camp.category.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || camp.status === statusFilter;
      const matchesCategory = categoryFilter === 'all' || camp.category === categoryFilter;
      return matchesSearch && matchesStatus && matchesCategory;
    });
  }, [campaigns, searchQuery, statusFilter, categoryFilter]);

  const totalPages = Math.ceil(filteredCampaigns.length / itemsPerPage) || 1;
  const paginatedCampaigns = filteredCampaigns.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div className={`${
      isEditing 
        ? 'fixed inset-0 z-[100] h-screen w-screen rounded-none border-none' 
        : 'ml-0 lg:ml-[256px] h-[calc(100vh-140px)] rounded-2xl border border-slate-800/80 shadow-2xl'
    } bg-[#090d16] text-gray-100 flex flex-col font-sans overflow-hidden transition-all duration-300`}>
      {/* Header */}
      <header className="px-6 py-3 bg-[#0d1322] border-b border-gray-800/60 flex items-center justify-between shrink-0">

        <div className="flex items-center gap-3">
          <FolderKanban className="w-6 h-6 text-purple-500" />
          <h1 className="text-xl font-bold tracking-tight text-white">
            {isEditing 
              ? `Campaign Editor - ${selectedCampaign?.name || ''}` 
              : 'Universal Campaign & Flow Builder'}
          </h1>
        </div>
        {isEditing && (
          <button
            onClick={() => {
              const isEditorRoute = window.location.pathname.includes('/campaigns/editor');
              if (isEditorRoute) {
                window.close();
              } else {
                setIsEditing(false);
                setSelectedCampaign(null);
              }
            }}
            className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition"
            title="Close Editor"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </header>

      {/* Main Content split */}
      <div className="flex-1 flex overflow-hidden">
        {/* Campaign List Sidebar */}
        {!isEditing ? (
          <div className="flex-1 bg-slate-950 p-8 flex flex-col overflow-y-auto">
            <div className="flex flex-wrap justify-end items-center gap-4 mb-6">
              <div className="flex flex-wrap sm:flex-nowrap gap-4 items-center w-full sm:w-auto justify-end">
                <input 
                  type="text" 
                  placeholder="Search campaigns..." 
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                  className="bg-slate-900 border border-slate-800 text-sm rounded-md px-3 py-2.5 text-white w-full sm:w-64 focus:outline-none focus:border-indigo-500 transition-colors"
                />
                <select 
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                  className="bg-slate-900 border border-slate-800 text-sm rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-indigo-500 cursor-pointer transition-colors w-full sm:w-auto"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="paused">Paused</option>
                </select>
                <button 
                  onClick={() => {
                    window.open('/campaigns/editor?mode=new', '_blank');
                  }}
                  className="flex items-center justify-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-sm font-semibold transition text-white w-full sm:w-auto shadow-sm"
                >
                  <Plus className="w-4 h-4" /> New Campaign
                </button>
              </div>
            </div>

            {/* Category Tabs */}
            <div className="flex border-b border-slate-800 mb-6 gap-2 overflow-x-auto">
              <button
                onClick={() => { setCategoryFilter('all'); setCurrentPage(1); }}
                className={`px-5 py-3 text-sm font-semibold transition-all relative whitespace-nowrap ${
                  categoryFilter === 'all' 
                    ? 'text-indigo-400 border-b-2 border-indigo-500' 
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                All Campaigns
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => { setCategoryFilter(cat); setCurrentPage(1); }}
                  className={`px-5 py-3 text-sm font-semibold transition-all relative capitalize whitespace-nowrap ${
                    categoryFilter === cat 
                      ? 'text-indigo-400 border-b-2 border-indigo-500' 
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {campaigns.length === 0 ? (
              <div className="text-sm text-slate-500 py-10 text-center bg-slate-900 rounded-lg border border-slate-800">
                No campaigns created yet. Click New Campaign to start.
              </div>
            ) : (
              <div className="w-full bg-slate-900 border border-slate-800 rounded-lg overflow-hidden shadow-sm">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-950/50 border-b border-slate-800 text-xs uppercase tracking-wider text-slate-400 font-bold">
                      <th className="p-4 font-bold">Name</th>
                      <th className="p-4 font-bold">Category</th>
                      <th className="p-4 font-bold">Status</th>
                      <th className="p-4 font-bold">Priority</th>
                      <th className="p-4 font-bold">Trigger</th>
                      <th className="p-4 font-bold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/50">
                    {paginatedCampaigns.length === 0 && (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-slate-500">No campaigns found matching your filters.</td>
                      </tr>
                    )}
                    {paginatedCampaigns.map((camp) => (
                      <tr 
                        key={camp.id} 
                        onClick={() => {
                          window.open(`/campaigns/editor?id=${camp.id}`, '_blank');
                        }}
                        className="hover:bg-slate-800/30 transition cursor-pointer group"
                      >
                        <td className="p-4 text-sm font-semibold text-slate-200">
                          {camp.name}
                        </td>
                        <td className="p-4 text-sm text-slate-400 capitalize">
                          {camp.category}
                        </td>
                        <td className="p-4">
                          <span className={`text-[10px] px-2.5 py-1 rounded-full font-bold uppercase ${
                            camp.status === 'active' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-slate-850 text-slate-400 border border-slate-700'
                          }`}>
                            {camp.status}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-slate-400">
                          {camp.priority}
                        </td>
                        <td className="p-4 text-sm text-indigo-400 font-semibold">
                          {camp.flow_json?.trigger?.event || 'N/A'}
                        </td>
                        <td className="p-4 text-right">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              if (camp.id) deleteCampaign(camp.id);
                            }}
                            className="p-2 text-slate-500 hover:bg-red-500/10 hover:text-red-400 rounded-lg transition"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Controls */}
            {campaigns.length > 0 && (
              <div className="flex items-center justify-between mt-6">
                <span className="text-sm text-slate-500">
                  Showing {filteredCampaigns.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredCampaigns.length)} of {filteredCampaigns.length} entries
                </span>
                <div className="flex gap-2">
                  <button 
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    className="px-4 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-lg text-sm text-white disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    Previous
                  </button>
                  <div className="flex items-center justify-center px-4 py-2 bg-slate-900 border border-slate-800 rounded-lg text-sm text-white font-semibold">
                    Page {currentPage} of {totalPages}
                  </div>
                  <button 
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    className="px-4 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-lg text-sm text-white disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Editor Split Layout */
          <div className="flex-1 flex flex-col md:flex-row-reverse overflow-hidden">
            {/* Control Sidebar (Form Settings + Custom Flow logic) */}
            <div className="w-full md:w-[350px] shrink-0 h-[45vh] md:h-full bg-[#0d1322] border-t md:border-t-0 md:border-l border-gray-850 flex flex-col overflow-hidden z-10 shadow-[0_-10px_30px_rgba(0,0,0,0.5)] md:shadow-none">
              {/* Tab Navigation */}
              <div className="flex border-b border-gray-800 shrink-0">
                <button
                  onClick={() => setActiveTab('flow')}
                  className={`flex-1 py-2 text-center text-xs font-semibold transition ${
                    activeTab === 'flow' ? 'text-purple-400 border-b-2 border-purple-500 bg-purple-500/5' : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  Automation Flow
                </button>
                <button
                  onClick={() => setActiveTab('display')}
                  className={`flex-1 py-2 text-center text-xs font-semibold transition ${
                    activeTab === 'display' ? 'text-purple-400 border-b-2 border-purple-500 bg-purple-500/5' : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  Display & Banner
                </button>
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
                {activeTab === 'flow' ? (
                  <>
                    {/* General Metadata */}
                    <div className="flex flex-col gap-3 bg-[#131b2e] p-3 rounded-xl border border-gray-800">
                      <h3 className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Campaign Info</h3>
                      
                      <div className="flex flex-col gap-1">
                        <label className="text-[10px] text-gray-400 font-medium">Campaign Name</label>
                        <input
                          type="text"
                          value={selectedCampaign?.name || ''}
                          onChange={(e) => handleCampaignFieldChange('name', e.target.value)}
                          className="bg-[#090d16] border border-gray-800 focus:border-purple-500 focus:outline-none rounded-lg px-2.5 py-1.5 text-xs text-white transition"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex flex-col gap-1">
                          <label className="text-[10px] text-gray-400 font-medium">Category</label>
                          <select
                            value={selectedCampaign?.category || 'promotions'}
                            onChange={(e) => handleCampaignFieldChange('category', e.target.value)}
                            className="bg-[#090d16] border border-gray-800 focus:border-purple-500 focus:outline-none rounded-lg px-2 py-1.5 text-xs text-white transition"
                          >
                            <option value="promotions">Promotions</option>
                            <option value="vip">VIP Rewards</option>
                            <option value="affiliate">Affiliate</option>
                            <option value="missions">Missions</option>
                            <option value="offers">Offers</option>
                          </select>
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[10px] text-gray-400 font-medium">Status</label>
                          <select
                            value={selectedCampaign?.status || 'draft'}
                            onChange={(e) => handleCampaignFieldChange('status', e.target.value)}
                            className="bg-[#090d16] border border-gray-800 focus:border-purple-500 focus:outline-none rounded-lg px-2 py-1.5 text-xs text-white transition"
                          >
                            <option value="draft">Draft (Paused)</option>
                            <option value="active">Active</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex flex-col gap-1">
                          <label className="text-[10px] text-gray-400 font-medium">Priority (0 - 99)</label>
                          <input
                            type="number"
                            value={selectedCampaign?.priority ?? 0}
                            onChange={(e) => handleCampaignFieldChange('priority', parseInt(e.target.value) || 0)}
                            className="bg-[#090d16] border border-gray-800 focus:border-purple-500 focus:outline-none rounded-lg px-2.5 py-1.5 text-xs text-white transition"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[10px] text-gray-400 font-medium">Claim Type</label>
                          <select
                            value={selectedCampaign?.flow_json?.claim_type || 'direct'}
                            onChange={(e) => {
                              if (selectedCampaign) {
                                setSelectedCampaign({
                                  ...selectedCampaign,
                                  flow_json: {
                                    ...selectedCampaign.flow_json,
                                    claim_type: e.target.value
                                  }
                                });
                              }
                            }}
                            className="bg-[#090d16] border border-gray-800 focus:border-purple-500 focus:outline-none rounded-lg px-2 py-1.5 text-xs text-white transition"
                          >
                            <option value="direct">Direct Credit</option>
                            <option value="claimable">Manual Claim</option>
                          </select>
                        </div>
                      </div>

                      <div className="flex gap-4 items-center">
                        <label className="flex items-center gap-1.5 text-[10px] text-gray-400 cursor-pointer py-1">
                          <input
                            type="checkbox"
                            checked={selectedCampaign?.flow_json?.repeatable ?? false}
                            onChange={(e) => {
                              if (selectedCampaign) {
                                setSelectedCampaign({
                                  ...selectedCampaign,
                                  flow_json: {
                                    ...selectedCampaign.flow_json,
                                    repeatable: e.target.checked
                                  }
                                });
                              }
                            }}
                            className="rounded border-gray-700 text-purple-600 focus:ring-purple-500 w-3 h-3"
                          />
                          Repeatable Claim
                        </label>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex flex-col gap-1">
                          <label className="text-[10px] text-gray-400 font-medium">Start Date</label>
                          <input
                            type="datetime-local"
                            value={selectedCampaign?.start_date?.substring(0, 16) || ''}
                            onChange={(e) => handleCampaignFieldChange('start_date', e.target.value)}
                            className="bg-[#090d16] border border-gray-800 focus:outline-none focus:border-purple-500 rounded-lg px-2 py-1.5 text-xs text-white"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[10px] text-gray-400 font-medium">End Date</label>
                          <input
                            type="datetime-local"
                            value={selectedCampaign?.end_date?.substring(0, 16) || ''}
                            onChange={(e) => handleCampaignFieldChange('end_date', e.target.value)}
                            className="bg-[#090d16] border border-gray-800 focus:outline-none focus:border-purple-500 rounded-lg px-2 py-1.5 text-xs text-white"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Nodes Action Center / Configurator */}
                    <div className="flex flex-col gap-4">
                      <div className="flex justify-between items-center">
                        <h3 className="text-xs uppercase font-bold text-gray-400 tracking-wider">Flow Canvas Controls</h3>
                        <div className="flex gap-2">
                          <button
                            onClick={() => addFlowElement('condition')}
                            className="px-2.5 py-1 text-xs font-semibold bg-blue-600 hover:bg-blue-500 rounded text-white transition"
                          >
                            + Condition
                          </button>
                          <button
                            onClick={() => addFlowElement('action')}
                            className="px-2.5 py-1 text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 rounded text-white transition"
                          >
                            + Action
                          </button>
                        </div>
                      </div>

                      {/* Selected Node Details Form */}
                      {selectedNodeId && selectedNodeData ? (
                        <div className="p-4 bg-[#131b2e] rounded-xl border-2 border-purple-500/30 flex flex-col gap-4">
                          <div className="flex items-center justify-between pb-2 border-b border-gray-800">
                            <span className="text-xs font-bold text-purple-400 uppercase tracking-wide">
                              Editing Node: {selectedNodeId}
                            </span>
                            {selectedNodeId !== 'trigger' && (
                              <button
                                onClick={() => {
                                  removeFlowElement(selectedNodeId);
                                }}
                                className="text-red-400 hover:text-red-300 transition"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>

                          {/* Render based on node type */}
                          {selectedNodeData.type === 'trigger' && (
                            <div className="flex flex-col gap-3">
                              <label className="text-xs text-gray-400">Trigger Event</label>
                              <select
                                value={selectedNodeData.data?.event || ''}
                                onChange={(e) => updateNodeData('trigger', { event: e.target.value })}
                                className="bg-[#090d16] border border-gray-800 rounded-lg px-3 py-2 text-sm text-white"
                              >
                                <option value="user_registered">User Registered</option>
                                <option value="user_login">User Login</option>
                                <option value="deposit_success">Deposit Success</option>
                                <option value="withdraw_success">Withdraw Success</option>
                                <option value="bet_settled">Bet Settled</option>
                                <option value="vip_updated">VIP Tier Updated</option>
                                <option value="affiliate_joined">Affiliate Partner Joined</option>
                                <option value="manual_trigger">Manual Admin Event</option>
                              </select>
                            </div>
                          )}

                          {selectedNodeData.type === 'condition' && (
                            <div className="flex flex-col gap-3">
                              <div className="flex flex-col gap-1">
                                <label className="text-xs text-gray-400">Field Target</label>
                                <select
                                  value={selectedNodeData.data?.field || ''}
                                  onChange={(e) => updateNodeData(selectedNodeId, { field: e.target.value })}
                                  className="bg-[#090d16] border border-gray-800 rounded-lg px-3 py-2 text-sm text-white"
                                >
                                  <option value="deposit_amount">Deposit Amount</option>
                                  <option value="withdraw_amount">Withdraw Amount</option>
                                  <option value="bet_amount">Bet Amount</option>
                                  <option value="profit">Net Profit</option>
                                  <option value="loss">Net Loss</option>
                                  <option value="turnover">Turnover (Wager)</option>
                                  <option value="lifetime_turnover">Lifetime Turnover</option>
                                  <option value="game_id">Game ID / Code</option>
                                  <option value="provider_id">Game Provider ID</option>
                                  <option value="aggregator_id">Game Aggregator ID</option>
                                  <option value="group_id">Game Group ID</option>
                                  <option value="lifetime_turnover_game">Lifetime Game-Specific Turnover</option>
                                  <option value="lifetime_turnover_provider">Lifetime Provider-Specific Turnover</option>
                                  <option value="lifetime_turnover_aggregator">Lifetime Aggregator-Specific Turnover</option>
                                  <option value="lifetime_turnover_group">Lifetime Group-Specific Turnover</option>
                                  <option value="vip_level">VIP Level (Rakeback Tier)</option>
                                  <option value="country">Country Code (Geo)</option>
                                  <option value="currency">Currency Code</option>
                                  <option value="first_deposit">Is First Deposit?</option>
                                  <option value="affiliate_exists">Has Referrer Partner?</option>
                                  <option value="referred_user">Is Referred User?</option>
                                  <option value="campaign_claimed">Has Already Claimed?</option>
                                </select>
                              </div>

                              <div className="grid grid-cols-2 gap-2">
                                <div className="flex flex-col gap-1">
                                  <label className="text-xs text-gray-400">Operator</label>
                                  <select
                                    value={selectedNodeData.data?.operator || ''}
                                    onChange={(e) => updateNodeData(selectedNodeId, { operator: e.target.value })}
                                    className="bg-[#090d16] border border-gray-800 rounded-lg px-3 py-2 text-sm text-white"
                                  >
                                    <option value=">=">&gt;=</option>
                                    <option value="<=">&lt;=</option>
                                    <option value=">">&gt;</option>
                                    <option value="<">&lt;</option>
                                    <option value="=">=</option>
                                    <option value="!=">!=</option>
                                  </select>
                                </div>
                                <div className="flex flex-col gap-1">
                                  <label className="text-xs text-gray-400">Value</label>
                                  <input
                                    type="text"
                                    value={selectedNodeData.data?.value ?? ''}
                                    onChange={(e) => {
                                      const rawVal = e.target.value;
                                      const parsed = isNaN(Number(rawVal)) || rawVal === '' ? rawVal : Number(rawVal);
                                      updateNodeData(selectedNodeId, { value: parsed });
                                    }}
                                    className="bg-[#090d16] border border-gray-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none"
                                  />
                                </div>
                              </div>
                            </div>
                          )}

                          {selectedNodeData.type === 'action' && (
                            <div className="flex flex-col gap-3">
                              <div className="flex flex-col gap-1">
                                <label className="text-xs text-gray-400">Action Type</label>
                                <select
                                  value={selectedNodeData.data?.type || ''}
                                  onChange={(e) => updateNodeData(selectedNodeId, { type: e.target.value })}
                                  className="bg-[#090d16] border border-gray-800 rounded-lg px-3 py-2 text-sm text-white"
                                >
                                  <option value="wallet_credit">Wallet Credit (Main)</option>
                                  <option value="wallet_debit">Wallet Debit (Main)</option>
                                  <option value="bonus_credit">Bonus Credit (Promo Wallet)</option>
                                  <option value="cashback">Weekly/Daily Cashback</option>
                                  <option value="lossback">Lossback Refund</option>
                                  <option value="accrue_rakeback">Accrue Rakeback</option>
                                  <option value="vip_upgrade">Upgrade VIP Tier</option>
                                  <option value="vip_downgrade">Downgrade VIP Tier</option>
                                  <option value="affiliate_commission">Affiliate Commission</option>
                                  <option value="send_notification">Send In-App Notification</option>
                                  <option value="assign_tag">Assign User Tag</option>
                                  <option value="remove_tag">Remove User Tag</option>
                                  <option value="ban_user">Ban User</option>
                                  <option value="unban_user">Unban User</option>
                                  <option value="lock_wallet">Lock Wallet</option>
                                  <option value="unlock_wallet">Unlock Wallet</option>
                                </select>
                              </div>
 
                              {/* Dynamic fields based on action */}
                              {['wallet_credit', 'wallet_debit', 'bonus_credit', 'cashback', 'lossback', 'affiliate_commission', 'accrue_rakeback'].includes(selectedNodeData.data?.type) && (
                                <div className="flex flex-col gap-2">
                                  <div className="grid grid-cols-2 gap-2">
                                    <div className="flex flex-col gap-1">
                                      <label className="text-xs text-gray-400">
                                        {selectedNodeData.data?.type === 'accrue_rakeback' ? 'Rakeback %' : 'Amount'}
                                      </label>
                                      <input
                                        type="number"
                                        value={selectedNodeData.data?.amount || 0}
                                        onChange={(e) => updateNodeData(selectedNodeId, { amount: parseFloat(e.target.value) || 0 })}
                                        className="bg-[#090d16] border border-gray-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none"
                                      />
                                    </div>
                                    {selectedNodeData.data?.type !== 'accrue_rakeback' && (
                                      <div className="flex flex-col gap-1">
                                        <label className="text-xs text-gray-400">Currency</label>
                                        <select
                                          value={selectedNodeData.data?.currency || 'inr'}
                                          onChange={(e) => updateNodeData(selectedNodeId, { currency: e.target.value })}
                                          className="bg-[#090d16] border border-gray-800 rounded-lg px-3 py-2 text-sm text-white max-h-48 overflow-y-auto"
                                        >
                                          <option value="dynamic">Same as Deposit/Bet</option>
                                          {dbCurrencies.length > 0 ? (
                                            dbCurrencies.map(c => (
                                              <option key={c.code} value={c.code}>{c.code.toUpperCase()} - {c.name}</option>
                                            ))
                                          ) : (
                                            <>
                                              <option value="inr">INR</option>
                                              <option value="usdttrc20">USDT (TRC20)</option>
                                              <option value="usdtmatic">USDT (MATIC)</option>
                                              <option value="btc">BTC</option>
                                              <option value="eth">ETH</option>
                                              <option value="trx">TRX</option>
                                            </>
                                          )}
                                        </select>
                                      </div>
                                    )}
                                  </div>
                                  {selectedNodeData.data?.type !== 'accrue_rakeback' && (
                                    <label className="flex items-center gap-1.5 text-[10px] text-gray-400 cursor-pointer py-1">
                                      <input
                                        type="checkbox"
                                        checked={selectedNodeData.data?.is_percentage ?? false}
                                        onChange={(e) => updateNodeData('action', { is_percentage: e.target.checked })}
                                      />
                                      Calculate as % of trigger value
                                    </label>
                                  )}
                                </div>
                              )}

                              {['vip_upgrade', 'vip_downgrade'].includes(selectedNodeData.data?.type) && (
                                <div className="flex flex-col gap-1">
                                  <label className="text-xs text-gray-400">Target Level Index</label>
                                  <input
                                    type="number"
                                    value={selectedNodeData.data?.level || 0}
                                    onChange={(e) => updateNodeData(selectedNodeId, { level: parseInt(e.target.value) || 0 })}
                                    className="bg-[#090d16] border border-gray-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none"
                                  />
                                </div>
                              )}

                              {['assign_tag', 'remove_tag'].includes(selectedNodeData.data?.type) && (
                                <div className="flex flex-col gap-1">
                                  <label className="text-xs text-gray-400">Tag String</label>
                                  <input
                                    type="text"
                                    value={selectedNodeData.data?.tag || ''}
                                    onChange={(e) => updateNodeData(selectedNodeId, { tag: e.target.value })}
                                    className="bg-[#090d16] border border-gray-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none"
                                  />
                                </div>
                              )}

                              {selectedNodeData.data?.type === 'send_notification' && (
                                <div className="flex flex-col gap-2">
                                  <div className="flex flex-col gap-1">
                                    <label className="text-xs text-gray-400">Message Title</label>
                                    <input
                                      type="text"
                                      value={selectedNodeData.data?.title || ''}
                                      onChange={(e) => updateNodeData(selectedNodeId, { title: e.target.value })}
                                      className="bg-[#090d16] border border-gray-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none"
                                    />
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <label className="text-xs text-gray-400">Message Body</label>
                                    <textarea
                                      value={selectedNodeData.data?.message || ''}
                                      onChange={(e) => updateNodeData(selectedNodeId, { message: e.target.value })}
                                      rows={3}
                                      className="bg-[#090d16] border border-gray-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="p-4 bg-[#131b2e] rounded-xl border border-gray-800 text-sm text-gray-400 flex items-center gap-2">
                          <AlertCircle className="w-5 h-5 text-purple-400" />
                          <span>Click a node on the canvas to edit its properties or logic.</span>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  /* Display rules tab styling */
                  <div className="flex flex-col gap-3 bg-[#131b2e] p-3 rounded-xl border border-gray-800">
                    <h3 className="text-[10px] uppercase font-bold text-purple-400 tracking-wider">Display Settings</h3>
                    
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] text-gray-400 font-medium">Display Title</label>
                      <input
                        type="text"
                        value={selectedCampaign?.display_json?.title || ''}
                        onChange={(e) => handleDisplayFieldChange('title', e.target.value)}
                        className="bg-[#090d16] border border-gray-800 rounded-lg px-2.5 py-1.5 text-xs text-white"
                      />
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] text-gray-400 font-medium">Display Description</label>
                      <textarea
                        value={selectedCampaign?.display_json?.description || ''}
                        onChange={(e) => handleDisplayFieldChange('description', e.target.value)}
                        rows={2}
                        className="bg-[#090d16] border border-gray-800 rounded-lg px-2.5 py-1.5 text-xs text-white"
                      />
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] text-gray-400 font-medium">Banner URL</label>
                      <input
                        type="text"
                        value={selectedCampaign?.display_json?.banner || ''}
                        onChange={(e) => handleDisplayFieldChange('banner', e.target.value)}
                        className="bg-[#090d16] border border-gray-800 rounded-lg px-2.5 py-1.5 text-xs text-white"
                      />
                      <div className="mt-1 flex items-center gap-2">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileUpload(file, 'banner');
                          }}
                          className="hidden"
                          id="banner-file-upload"
                        />
                        <label
                          htmlFor="banner-file-upload"
                          className="cursor-pointer px-2.5 py-1 text-[10px] font-semibold bg-gray-800 hover:bg-gray-700 rounded text-gray-300 transition"
                        >
                          {uploadingBanner ? 'Uploading...' : 'Upload Banner'}
                        </label>
                        {selectedCampaign?.display_json?.banner && (
                          <div className="w-12 h-6 border border-gray-800 rounded overflow-hidden">
                            <img
                              src={selectedCampaign.display_json.banner.startsWith('http') ? selectedCampaign.display_json.banner : `${API_BASE}${selectedCampaign.display_json.banner}`}
                              className="w-full h-full object-cover"
                              alt="Banner Preview"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = '/assets/bcimages/bchome/bcgame-home-banner.webp';
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] text-gray-400 font-medium">Icon URL</label>
                      <input
                        type="text"
                        value={selectedCampaign?.display_json?.icon || ''}
                        onChange={(e) => handleDisplayFieldChange('icon', e.target.value)}
                        className="bg-[#090d16] border border-gray-800 rounded-lg px-2.5 py-1.5 text-xs text-white"
                      />
                      <div className="mt-1 flex items-center gap-2">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileUpload(file, 'icon');
                          }}
                          className="hidden"
                          id="icon-file-upload"
                        />
                        <label
                          htmlFor="icon-file-upload"
                          className="cursor-pointer px-2.5 py-1 text-[10px] font-semibold bg-gray-800 hover:bg-gray-700 rounded text-gray-300 transition"
                        >
                          {uploadingIcon ? 'Uploading...' : 'Upload Icon'}
                        </label>
                        {selectedCampaign?.display_json?.icon && (
                          <div className="w-6 h-6 border border-gray-800 rounded overflow-hidden flex items-center justify-center bg-[#090d16]">
                            <img
                              src={selectedCampaign.display_json.icon.startsWith('http') ? selectedCampaign.display_json.icon : `${API_BASE}${selectedCampaign.display_json.icon}`}
                              className="w-full h-full object-contain"
                              alt="Icon Preview"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = '/assets/bcimages/promotion/starimg.webp';
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>


                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex flex-col gap-1">
                        <label className="text-[10px] text-gray-400 font-medium">Sort Order</label>
                        <input
                          type="number"
                          value={selectedCampaign?.display_json?.sort_order ?? 1}
                          onChange={(e) => handleDisplayFieldChange('sort_order', parseInt(e.target.value) || 1)}
                          className="bg-[#090d16] border border-gray-800 rounded-lg px-2.5 py-1.5 text-xs text-white"
                        />
                      </div>
                      <div className="flex flex-col gap-1 justify-end">
                        <label className="flex items-center gap-1.5 text-[10px] text-gray-400 cursor-pointer py-1.5">
                          <input
                            type="checkbox"
                            checked={selectedCampaign?.display_json?.visible ?? true}
                            onChange={(e) => handleDisplayFieldChange('visible', e.target.checked)}
                            className="rounded border-gray-700 text-purple-600 focus:ring-purple-500 w-3 h-3"
                          />
                          Visible to users
                        </label>
                      </div>
                    </div>

                    {/* Show on Pages (checkboxes) */}
                    <div className="flex flex-col gap-2">
                      <label className="text-xs text-gray-400">Display Placement Pages</label>
                      <div className="grid grid-cols-2 gap-2 bg-[#090d16] p-3 rounded-lg border border-gray-850">
                        {['home', 'promotions', 'vip', 'affiliate', 'missions', 'offers', 'popup'].map((pgName) => {
                          const showOn = selectedCampaign?.display_json?.show_on || [];
                          const isChecked = showOn.includes(pgName);
                          return (
                            <label key={pgName} className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer capitalize">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  let newShowOn = [...showOn];
                                  if (e.target.checked) {
                                    newShowOn.push(pgName);
                                  } else {
                                    newShowOn = newShowOn.filter(p => p !== pgName);
                                  }
                                  handleDisplayFieldChange('show_on', newShowOn);
                                }}
                                className="rounded border-gray-700 text-purple-600 focus:ring-purple-500"
                              />
                              {pgName}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Editor Buttons */}
              <div className="p-4 bg-[#0d1322] border-t border-gray-800 flex gap-3">
                <button
                  onClick={saveCampaign}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-semibold transition"
                >
                  <Save className="w-4 h-4" /> Save Campaign
                </button>
                <button
                  onClick={() => {
                    const isEditorRoute = window.location.pathname.includes('/campaigns/editor');
                    if (isEditorRoute) {
                      window.close();
                    } else {
                      setIsEditing(false);
                      setSelectedCampaign(null);
                    }
                  }}
                  className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-sm font-semibold transition"
                >
                  Cancel
                </button>
              </div>
            </div>

            {/* Interactive React Flow Workspace Panel */}
            <div className="flex-1 bg-[#090d16] relative">
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeClick={onNodeClick}
                nodeTypes={nodeTypes}
                fitView
                fitViewOptions={{ maxZoom: 0.75 }}
              >
                <Controls />

                <Background color="#1e293b" gap={16} size={1} />
              </ReactFlow>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
